import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import axios from 'axios';
import { IntlProvider } from 'react-intl';
import '@formatjs/intl-pluralrules/polyfill';
import '@formatjs/intl-pluralrules/locale-data/ru';
import '@formatjs/intl-pluralrules/locale-data/uk';
import messagesUk from './locale/uk.json';
import messagesRu from './locale/ru.json';
import '../Building/BuildingMenu/BuildingMenu';
import '../DeveloperQuestionPopup/DeveloperQuestionPopup';
import FlatsMap from './FlatsMap/FlatsMap';
import FlatsMenu from './FlatsMenu/FlatsMenu';
import {
  FlatsFiltersContentContext,
  FlatsFiltersOpenedContext,
  FlatsFiltersEmptyStatesContext,
} from '../FlatsFilters/FlatsFiltersContext';
import { getIsPreferredCurrencyUsd } from '../UiKit/Blocks/CurrencySwitcher/CurrencySwitcher';
import { syncFavorite } from '../helpers/favorites';
import { loader } from '../helpers/loader';
import { replaceUrl } from '../helpers/query-string';
import './Flats.scss';
import PlanModal from '../PlanModal/PlanModal';
import { EMPTY_STATES } from './FlatsEmptyStates/FlatsEmptyStates';
import { CURRENCY } from '../helpers/currency';
import FlatsMapLabel from './FlatsMapLabel/FlatsMapLabel';
import { initViewHeight } from '../helpers/view-height';

const getActiveFiltersCount = (params) => {
  const activeFilters = Object.entries(params).filter(([key, value]) => {
    if (key === 'building_id') {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length !== 0;
    }
    return value;
  });

  return activeFilters.length;
};

const getEmptyState = (params) => {
  if (
    params.section_id.every(
      (id) => window.params.sectionDeveloperOffers[id] === EMPTY_STATES.NOT_STARTED,
    )
  ) {
    return EMPTY_STATES.NOT_STARTED;
  }

  if (
    params.section_id.every(
      (id) => window.params.sectionDeveloperOffers[id] === EMPTY_STATES.ALL_SOLD,
    )
  ) {
    return EMPTY_STATES.ALL_SOLD;
  }

  if (
    params.house_id.every(
      (id) => window.params.houseDeveloperOffers[id] === EMPTY_STATES.NOT_STARTED,
    )
  ) {
    return EMPTY_STATES.NOT_STARTED;
  }

  if (
    params.house_id.every((id) => window.params.houseDeveloperOffers[id] === EMPTY_STATES.ALL_SOLD)
  ) {
    return EMPTY_STATES.ALL_SOLD;
  }

  return EMPTY_STATES.NOT_FOUND;
};

const Flats = () => {
  const [searchParams, setSearchParams] = useState(window.params.searchParams);
  const [activeFiltersCount, setActiveFiltersCount] = useState(getActiveFiltersCount(searchParams));
  const [flatsCount, setFlatsCount] = useState(0);
  const [opened, setOpened] = useState(false);
  const [menuContent, setMenuContent] = useState(<div />);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [emptyState, setEmptyState] = useState(EMPTY_STATES.NOT_FOUND);
  const currency = useMemo(() => (getIsPreferredCurrencyUsd() ? CURRENCY.USD : CURRENCY.UAH), []);
  const locale = window.currentLanguage;
  const messages = {
    uk: messagesUk,
    ru: messagesRu,
  };
  const isFirstRender = useRef(true);
  const [shouldRenderMap, setShouldRenderMap] = useState(!window.isMobile);

  const updateSearchParams = (params) => {
    /**
     * Add support of functional updates: using function that accept
     * the current value and returns the new value
     *  */

    if (typeof params === 'function') {
      setSearchParams((currentSearchParams) => {
        const nextSearchParams = params(currentSearchParams);

        setActiveFiltersCount(getActiveFiltersCount(nextSearchParams));

        return nextSearchParams;
      });
    } else {
      const newParams = { ...params };
      setActiveFiltersCount(getActiveFiltersCount(newParams));
      setSearchParams(newParams);
    }
  };

  useEffect(() => {
    const updateContent = (params) => {
      loader.show();
      setIsUpdating(true);
      axios({
        url: '/api/filter/layout-pro',
        method: 'post',
        headers: {
          'X-CSRF-Token': window.csrf,
          'Content-type': 'application/json',
        },
        withCredentials: true,
        data: {
          building_id: window.params.buildingId,
          ...params,
        },
      })
        .then((response) => {
          loader.hide();
          if (!isFirstRender.current) {
            replaceUrl({ path: window.params.baseUrl, params });
          }
          isFirstRender.current = false;
          setMenuContent(response.data.params);
          window.params.modalsData = response.data.modalsData;
          setFlatsCount(response.data.totalFlatsCount);
          syncFavorite();
          setIsLoading(false);
          setIsUpdating(false);
          setEmptyState(
            response.data.totalFlatsCount === 0 ? getEmptyState(params) : EMPTY_STATES.NOT_FOUND,
          );
        })
        .catch((error) => {
          console.warn(error);
        });
    };
    updateContent(searchParams);
  }, [searchParams]);

  useEffect(() => {
    if (isMapVisible && !shouldRenderMap) {
      setShouldRenderMap(true);
    }
  }, [isMapVisible, shouldRenderMap]);

  return (
    <IntlProvider locale={locale} messages={messages[locale]}>
      <FlatsFiltersOpenedContext.Provider value={[opened, setOpened]}>
        <FlatsFiltersEmptyStatesContext.Provider
          value={{
            emptyState,
            setEmptyState,
          }}
        >
          <FlatsFiltersContentContext.Provider
            value={{
              searchParams,
              updateSearchParams,
              flatsCount,
              activeFiltersCount,
              isUpdating,
              currency,
              isMapVisible,
              setIsMapVisible,
            }}
          >
            {!isLoading && (
              <>
                <FlatsMenu lastUpdate={window.params.lastUpdate} menuContent={menuContent} />
                <FlatsMapLabel />
              </>
            )}
            {shouldRenderMap && <FlatsMap buildingId={window.params.buildingId} />}
          </FlatsFiltersContentContext.Provider>
        </FlatsFiltersEmptyStatesContext.Provider>
      </FlatsFiltersOpenedContext.Provider>
    </IntlProvider>
  );
};

const initFlatsPrototype = () => {
  const { flatId } = window.params.searchParams;
  initViewHeight();

  if (flatId) {
    new PlanModal({
      modalId: flatId,
      requestUrl: '/api/layout-pro-modal',
      openedModalKey: 'flatId',
    });
  }

  const container = document.querySelector('#flats-prototype');
  if (container) {
    const root = createRoot(container);
    root.render(<Flats />);
  }
};

initFlatsPrototype();
